// ============================================================
// llm-input-builder.ts
// ============================================================

import { llm } from '@cotera/api';
import type {
  ModelMessage,
  LanguageModel,
  ToolSet,
  ToolChoice,
  TelemetrySettings,
  streamText,
  ToolCallRepairFunction,
  StopCondition,
  PrepareStepFunction,
  StreamTextOnChunkCallback,
  StreamTextOnErrorCallback,
  StreamTextOnFinishCallback,
  StreamTextOnStepFinishCallback,
} from 'ai';
import type { Ty } from '@cotera/era';
import type { ZodType } from 'zod';
import * as yaml from 'js-yaml';
import { encodeValueToToon } from './toon-encode';
import type {
  ComprehensionEncoding,
  Fact,
  GenerationEncoding,
  IngotSchema,
  LlmArtifactDescriptor,
  LlmCallSettings,
  LlmConversationPromptOptions,
  LlmLoadedAgentContent,
  LlmLoadedResourceDescriptor,
  LlmPreparedToolSet,
  LlmPromptMessage,
  LlmPromptTrigger,
  LlmProviderOptions,
  LlmSystemInput,
  Receipt,
  RenderContext,
  Skill,
  LLMInput,
  LlmBuildRenderer,
  Renderer,
} from './llm-input-types';
export type {
  ComprehensionEncoding,
  Encoding,
  Fact,
  GenerationEncoding,
  IngotSchema,
  LlmArtifactDescriptor,
  LlmCallSettings,
  LlmConversationPromptOptions,
  LlmLoadedAgentContent,
  LlmLoadedResourceDescriptor,
  LlmPreparedTool,
  LlmPreparedToolSet,
  LlmPromptMessage,
  LlmPromptTrigger,
  LlmProviderOptions,
  LlmSystemInput,
  LlmWorkspaceDomainLoader,
  LLMInput,
  Receipt,
  RenderContext,
  Renderer,
  Skill,
  LlmBuildRenderer,
  LlmConversationMessagesRendererFactory,
} from './llm-input-types';
import type { ModelInputDraft } from './model-input-base';
import type { ModelInputAdapter } from './model-input-adapter';
import type { ModelInputBuildResult } from './model-input-build-result';
import objectHash from 'object-hash';

// ============================================================
// Encoding
// ============================================================

export function encode(
  value: unknown,
  encoding: ComprehensionEncoding
): string {
  switch (encoding) {
    case 'yaml':
      return yaml.dump(value);
    case 'json':
      return JSON.stringify(value, null, 2);
    case 'toon':
      return encodeValueToToon(value);
    case 'markdown':
      return String(value);
    case 'text':
      return String(value);
  }
}

type StreamTextParams = Parameters<typeof streamText>[0];

type GenerationEncodingSpec =
  | { kind: 'text' }
  | { kind: 'json'; schema?: ZodType }
  | { kind: 'yaml' }
  | { kind: 'markdown' };

// ============================================================
// Builder state (immutable — each method returns a new instance)
// ============================================================

type BuilderState = {
  /** Cotera messages for {@link ConversationRenderer} and generic renderers. */
  messageInstances: LlmPromptMessage[];
  facts: Fact[];
  skills: Skill[];
  receipts: Map<string, Receipt>;
  ingotSchema: IngotSchema | null;
  includeTime: boolean;
  _time?: Date;

  comprehensionEncoding: ComprehensionEncoding;
  generationSpec: GenerationEncodingSpec;

  system?: LlmSystemInput;
  temperature?: number;
  topP?: number;
  topK?: number;
  presencePenalty?: number;
  frequencyPenalty?: number;
  stopSequences?: string[];
  seed?: number;
  maxOutputTokens?: number;

  tools?: ToolSet;
  toolChoice?: ToolChoice<ToolSet>;
  activeTools?: string[];

  maxRetries?: number;
  abortSignal?: AbortSignal;
  timeout?: StreamTextParams['timeout'];
  headers?: Record<string, string | undefined>;

  output?: LLMInput['output'];
  /** Overrides {@link generationSpec} output when set (e.g. single-pass structured output). */
  streamTextOutput?: LLMInput['output'];
  /** Era type for single-pass output; resolved by the {@link ModelInputAdapter} at build. */
  outputShape?: Ty.ExtendedAttributeType;
  modelAdapter?: ModelInputAdapter;
  llmPreparedTools?: LlmPreparedToolSet;
  providerOptions?: LlmProviderOptions;
  experimental_telemetry?: TelemetrySettings;

  stopWhen?: StreamTextParams['stopWhen'];
  prepareStep?: PrepareStepFunction<ToolSet>;
  experimental_repairToolCall?: ToolCallRepairFunction<ToolSet>;
  experimental_transform?: StreamTextParams['experimental_transform'];
  experimental_download?: StreamTextParams['experimental_download'];
  includeRawChunks?: boolean;
  experimental_context?: unknown;

  onChunk?: StreamTextOnChunkCallback<ToolSet>;
  onError?: StreamTextOnErrorCallback;
  onFinish?: StreamTextOnFinishCallback<ToolSet>;
  onAbort?: StreamTextParams['onAbort'];
  onStepFinish?: StreamTextOnStepFinishCallback<ToolSet>;

  /** Conversation render options (see {@link LlmConversationPromptOptions}). */
  conversationPromptOptions?: LlmConversationPromptOptions;
  /** Tool keys resolved when the conversation prompt was prepared. */
  resolvedToolKeys?: readonly string[];
  /** Run trigger; non-chat runs omit default coco tools except memory (see {@link Conversation.getToolKeys}). */
  trigger?: LlmPromptTrigger | null;
  /** Parent chat id when this conversation is a nested sub-agent run. */
  parentConversationId?: string | null;
  /** Saved agent linked on the chat (`agentId`); affects default coco tools for Slack. */
  hasLinkedAgent?: boolean;
  /** Tool keys this chat must not invoke unless overridden by {@link explicitlyIncludedToolKeys}. */
  excludedToolKeys?: readonly string[];
  /** Tool keys from @mentions in the system prompt; override {@link excludedToolKeys}. */
  explicitlyIncludedToolKeys?: readonly string[];

  /** Coco active-context inputs; rendered via {@link ConversationRenderer}. */
  artifacts: LlmArtifactDescriptor[];
  documentReferenceIds: string[];
  loadedResources: LlmLoadedResourceDescriptor[];
  focusedAgentId: string | null;
  loadedAgentContents: LlmLoadedAgentContent[];
  loadedToolKeys: string[];
};

const DEFAULT_STATE: BuilderState = {
  messageInstances: [],
  facts: [],
  skills: [],
  receipts: new Map(),
  ingotSchema: null,
  includeTime: false,
  comprehensionEncoding: 'yaml',
  generationSpec: { kind: 'text' },
  artifacts: [],
  documentReferenceIds: [],
  loadedResources: [],
  focusedAgentId: null,
  loadedAgentContents: [],
  loadedToolKeys: [],
};

// ============================================================
// Builder
// ============================================================

export class LLMInputBuilder {
  private readonly s: BuilderState;

  constructor(state: BuilderState = DEFAULT_STATE) {
    this.s = state;
  }

  private clone(patch: Partial<BuilderState>): LLMInputBuilder {
    return new LLMInputBuilder({
      ...this.s,
      ...patch,
      receipts: patch.receipts ?? new Map(this.s.receipts),
    });
  }

  // ── Content ──────────────────────────────────────────────

  /** Conversation / Cotera messages for {@link ConversationRenderer} and generic renderers. */
  withMessages(messages: readonly LlmPromptMessage[]): LLMInputBuilder {
    return this.clone({ messageInstances: [...messages] });
  }

  withFacts(facts: Fact[]): LLMInputBuilder {
    return this.clone({ facts: [...this.s.facts, ...facts] });
  }

  withSkill(skill: Skill): LLMInputBuilder {
    return this.clone({ skills: [...this.s.skills, skill] });
  }

  withArtifact(artifact: LlmArtifactDescriptor): LLMInputBuilder {
    return this.clone({ artifacts: [...this.s.artifacts, artifact] });
  }

  withArtifacts(artifacts: readonly LlmArtifactDescriptor[]): LLMInputBuilder {
    return this.clone({ artifacts: [...this.s.artifacts, ...artifacts] });
  }

  withDocument(documentId: string): LLMInputBuilder {
    if (documentId.length === 0) {
      return this;
    }
    if (this.s.documentReferenceIds.includes(documentId)) {
      return this;
    }
    return this.clone({
      documentReferenceIds: [...this.s.documentReferenceIds, documentId],
    });
  }

  withDocuments(documentIds: readonly string[]): LLMInputBuilder {
    // eslint-disable-next-line @typescript-eslint/no-this-alias -- immutable builder fold
    let builder: LLMInputBuilder = this;
    for (const id of documentIds) {
      builder = builder.withDocument(id);
    }
    return builder;
  }

  withLoadedResource(resource: LlmLoadedResourceDescriptor): LLMInputBuilder {
    return this.clone({
      loadedResources: [...this.s.loadedResources, resource],
    });
  }

  withLoadedResources(
    resources: readonly LlmLoadedResourceDescriptor[]
  ): LLMInputBuilder {
    return this.clone({
      loadedResources: [...this.s.loadedResources, ...resources],
    });
  }

  withFocusedAgentId(agentId: string | null): LLMInputBuilder {
    return this.clone({ focusedAgentId: agentId });
  }

  withLoadedAgentContent(content: LlmLoadedAgentContent): LLMInputBuilder {
    return this.clone({
      loadedAgentContents: [...this.s.loadedAgentContents, content],
    });
  }

  withLoadedAgentContents(
    contents: readonly LlmLoadedAgentContent[]
  ): LLMInputBuilder {
    return this.clone({
      loadedAgentContents: [...this.s.loadedAgentContents, ...contents],
    });
  }

  withLoadedToolKey(toolKey: string): LLMInputBuilder {
    if (this.s.loadedToolKeys.includes(toolKey)) {
      return this;
    }
    return this.clone({
      loadedToolKeys: [...this.s.loadedToolKeys, toolKey],
    });
  }

  withLoadedToolKeys(toolKeys: readonly string[]): LLMInputBuilder {
    // eslint-disable-next-line @typescript-eslint/no-this-alias -- immutable builder fold
    let builder: LLMInputBuilder = this;
    for (const key of toolKeys) {
      builder = builder.withLoadedToolKey(key);
    }
    return builder;
  }

  withReceipt(receipt: Receipt): LLMInputBuilder {
    const next = new Map(this.s.receipts);
    next.set(receipt.toolCallId, receipt);
    return this.clone({ receipts: next });
  }

  withReceipts(receipts: readonly Receipt[]): LLMInputBuilder {
    const next = new Map(this.s.receipts);
    receipts.forEach((receipt) => {
      next.set(receipt.toolCallId, receipt);
    });
    return this.clone({ receipts: next });
  }

  getReceipts(): ReadonlyMap<string, Receipt> {
    return this.s.receipts;
  }

  withIngotSchema(schema: IngotSchema): LLMInputBuilder {
    return this.clone({ ingotSchema: schema });
  }

  getIngotSchema(): IngotSchema | null {
    return this.s.ingotSchema;
  }

  withTime(date?: Date): LLMInputBuilder {
    return this.clone({ includeTime: true, _time: date });
  }

  /**
   * Conversation render options for {@link ConversationRenderer}.
   * Set by {@link Conversation.toLlmInput}, not usually called directly.
   */
  withConversationPromptOptions(
    options: LlmConversationPromptOptions
  ): LLMInputBuilder {
    return this.clone({ conversationPromptOptions: options });
  }

  /** Tool keys for this prompt (resolved from conversation + extras at prepare time). */
  withResolvedToolKeys(keys: readonly string[]): LLMInputBuilder {
    return this.clone({ resolvedToolKeys: keys });
  }

  /** {@link ChatTriggerSource} for this prompt (from conversation row or ATC run). */
  withTrigger(trigger: LlmPromptTrigger | null): LLMInputBuilder {
    return this.clone({ trigger });
  }

  /** Set when this chat is a child conversation (sub-agent). */
  withParentConversationId(
    parentConversationId: string | null
  ): LLMInputBuilder {
    return this.clone({ parentConversationId });
  }

  withHasLinkedAgent(hasLinkedAgent: boolean): LLMInputBuilder {
    return this.clone({ hasLinkedAgent });
  }

  withExcludedToolKeys(excludedToolKeys: readonly string[]): LLMInputBuilder {
    return this.clone({ excludedToolKeys });
  }

  getExcludedToolKeys(): readonly string[] {
    return this.s.excludedToolKeys ?? [];
  }

  withExplicitlyIncludedToolKeys(
    explicitlyIncludedToolKeys: readonly string[]
  ): LLMInputBuilder {
    return this.clone({ explicitlyIncludedToolKeys });
  }

  getExplicitlyIncludedToolKeys(): readonly string[] {
    return this.s.explicitlyIncludedToolKeys ?? [];
  }

  getMessages(): LlmPromptMessage[] {
    return this.s.messageInstances;
  }

  getConversationPromptOptions(): LlmConversationPromptOptions | undefined {
    return this.s.conversationPromptOptions;
  }

  getResolvedToolKeys(): readonly string[] {
    return this.s.resolvedToolKeys ?? [];
  }

  getTrigger(): LlmPromptTrigger | null {
    return this.s.trigger ?? null;
  }

  getParentConversationId(): string | null {
    return this.s.parentConversationId ?? null;
  }

  getHasLinkedAgent(): boolean {
    return this.s.hasLinkedAgent === true;
  }

  getArtifacts(): readonly LlmArtifactDescriptor[] {
    return this.s.artifacts;
  }

  getDocumentReferenceIds(): readonly string[] {
    return this.s.documentReferenceIds;
  }

  getLoadedResources(): readonly LlmLoadedResourceDescriptor[] {
    return this.s.loadedResources;
  }

  getFocusedAgentId(): string | null {
    return this.s.focusedAgentId;
  }

  getLoadedAgentContents(): readonly LlmLoadedAgentContent[] {
    return this.s.loadedAgentContents;
  }

  getLoadedToolKeys(): readonly string[] {
    return this.s.loadedToolKeys;
  }

  getFacts(): readonly Fact[] {
    return this.s.facts;
  }

  getSkills(): readonly Skill[] {
    return this.s.skills;
  }

  getSystem(): LlmSystemInput | null {
    return this.s.system ?? null;
  }

  createRenderContext(): RenderContext {
    return {
      comprehensionEncoding: this.s.comprehensionEncoding,
      generationEncoding: this.s.generationSpec.kind,
      time: this.s.includeTime ? this.s._time ?? new Date() : undefined,
      encode: (value) => encode(value, this.s.comprehensionEncoding),
    };
  }

  withSystem(system: LlmSystemInput): LLMInputBuilder {
    return this.clone({ system });
  }

  // ── Encoding ─────────────────────────────────────────────

  withComprehensionEncoding(encoding: ComprehensionEncoding): LLMInputBuilder {
    return this.clone({ comprehensionEncoding: encoding });
  }

  /**
   * .withGenerationEncoding('text')
   * .withGenerationEncoding('json')              // prompt-instructed JSON, no schema
   * .withGenerationEncoding('json', MyZodSchema) // Output.object() with schema validation
   */
  withGenerationEncoding(
    kind: GenerationEncoding,
    schema?: ZodType
  ): LLMInputBuilder {
    const spec: GenerationEncodingSpec =
      kind === 'json' && schema !== undefined
        ? { kind: 'json', schema }
        : { kind: kind as GenerationEncodingSpec['kind'] };
    return this.clone({ generationSpec: spec });
  }

  // ── Sampling ─────────────────────────────────────────────

  withTemperature(value: number): LLMInputBuilder {
    return this.clone({ temperature: value });
  }

  withTopP(value: number): LLMInputBuilder {
    return this.clone({ topP: value });
  }

  withTopK(value: number): LLMInputBuilder {
    return this.clone({ topK: value });
  }

  withPresencePenalty(value: number): LLMInputBuilder {
    return this.clone({ presencePenalty: value });
  }

  withFrequencyPenalty(value: number): LLMInputBuilder {
    return this.clone({ frequencyPenalty: value });
  }

  withStopSequences(sequences: string[]): LLMInputBuilder {
    return this.clone({ stopSequences: sequences });
  }

  withSeed(value: number): LLMInputBuilder {
    return this.clone({ seed: value });
  }

  withMaxOutputTokens(value: number): LLMInputBuilder {
    return this.clone({ maxOutputTokens: value });
  }

  /** Merge model-specific call settings (e.g. from server `transformGenerateParams`). */
  withCallSettings(settings: LlmCallSettings): LLMInputBuilder {
    // eslint-disable-next-line @typescript-eslint/no-this-alias -- immutable builder fold
    let next: LLMInputBuilder = this;
    if (settings.temperature !== undefined) {
      next = next.withTemperature(settings.temperature);
    }
    if (settings.topP !== undefined) {
      next = next.withTopP(settings.topP);
    }
    if (settings.topK !== undefined) {
      next = next.withTopK(settings.topK);
    }
    if (settings.presencePenalty !== undefined) {
      next = next.withPresencePenalty(settings.presencePenalty);
    }
    if (settings.frequencyPenalty !== undefined) {
      next = next.withFrequencyPenalty(settings.frequencyPenalty);
    }
    if (settings.stopSequences !== undefined) {
      next = next.withStopSequences(settings.stopSequences);
    }
    if (settings.seed !== undefined) {
      next = next.withSeed(settings.seed);
    }
    if (settings.maxOutputTokens !== undefined) {
      next = next.withMaxOutputTokens(settings.maxOutputTokens);
    }
    if (settings.maxRetries !== undefined) {
      next = next.withMaxRetries(settings.maxRetries);
    }
    if (settings.providerOptions !== undefined) {
      next = next.withProviderOptions(settings.providerOptions);
    }
    return next;
  }

  withStreamTextOutput(output: LLMInput['output']): LLMInputBuilder {
    return this.clone({ streamTextOutput: output });
  }

  withOutputShape(outputShape: Ty.ExtendedAttributeType): LLMInputBuilder {
    return this.clone({ outputShape });
  }

  withLlmPreparedTools(tools: LlmPreparedToolSet): LLMInputBuilder {
    return this.clone({
      llmPreparedTools: { ...this.s.llmPreparedTools, ...tools },
    });
  }

  // ── Tools ────────────────────────────────────────────────

  withTools(tools: ToolSet): LLMInputBuilder {
    return this.clone({ tools: { ...this.s.tools, ...tools } });
  }

  withToolChoice(choice: ToolChoice<ToolSet>): LLMInputBuilder {
    return this.clone({ toolChoice: choice });
  }

  withActiveTools(names: string[]): LLMInputBuilder {
    return this.clone({ activeTools: names });
  }

  // ── Execution ─────────────────────────────────────────────

  withMaxRetries(value: number): LLMInputBuilder {
    return this.clone({ maxRetries: value });
  }

  withAbortSignal(signal: AbortSignal): LLMInputBuilder {
    return this.clone({ abortSignal: signal });
  }

  withTimeout(timeout: StreamTextParams['timeout']): LLMInputBuilder {
    return this.clone({ timeout });
  }

  withHeaders(headers: Record<string, string | undefined>): LLMInputBuilder {
    return this.clone({ headers });
  }

  // ── Provider options ─────────────────────────────────────

  withProviderOptions(opts: LlmProviderOptions): LLMInputBuilder {
    const merged: LlmProviderOptions = { ...this.s.providerOptions };
    for (const [provider, value] of Object.entries(opts)) {
      merged[provider] = {
        ...(merged[provider] as object | undefined),
        ...(value as object),
      };
    }
    return this.clone({ providerOptions: merged });
  }

  // ── Telemetry ────────────────────────────────────────────

  withTelemetry(settings: TelemetrySettings): LLMInputBuilder {
    return this.clone({ experimental_telemetry: settings });
  }

  // ── streamText hooks (used by step-chat, playground, etc.) ──

  withStopWhen(condition: StopCondition<ToolSet>): LLMInputBuilder {
    return this.clone({ stopWhen: condition });
  }

  withPrepareStep(fn: PrepareStepFunction<ToolSet>): LLMInputBuilder {
    return this.clone({ prepareStep: fn });
  }

  withRepairToolCall(fn: ToolCallRepairFunction<ToolSet>): LLMInputBuilder {
    return this.clone({ experimental_repairToolCall: fn });
  }

  withTransform(
    transform: StreamTextParams['experimental_transform']
  ): LLMInputBuilder {
    return this.clone({ experimental_transform: transform });
  }

  withDownload(
    fn: NonNullable<StreamTextParams['experimental_download']>
  ): LLMInputBuilder {
    return this.clone({ experimental_download: fn });
  }

  withIncludeRawChunks(include: boolean): LLMInputBuilder {
    return this.clone({ includeRawChunks: include });
  }

  withExperimentalContext(context: unknown): LLMInputBuilder {
    return this.clone({ experimental_context: context });
  }

  withOnChunk(callback: StreamTextOnChunkCallback<ToolSet>): LLMInputBuilder {
    return this.clone({ onChunk: callback });
  }

  withOnError(callback: StreamTextOnErrorCallback): LLMInputBuilder {
    return this.clone({ onError: callback });
  }

  withOnFinish(callback: StreamTextOnFinishCallback<ToolSet>): LLMInputBuilder {
    return this.clone({ onFinish: callback });
  }

  withOnAbort(callback: StreamTextParams['onAbort']): LLMInputBuilder {
    return this.clone({ onAbort: callback });
  }

  withOnStepFinish(
    callback: StreamTextOnStepFinishCallback<ToolSet>
  ): LLMInputBuilder {
    return this.clone({ onStepFinish: callback });
  }

  /** Select provider-specific augmentations applied in {@link build}. */
  model(adapter: ModelInputAdapter): LLMInputBuilder {
    return this.clone({ modelAdapter: adapter });
  }

  // ── Build ────────────────────────────────────────────────

  async build(renderer: LlmBuildRenderer): Promise<ModelInputBuildResult> {
    if (this.s.modelAdapter === undefined) {
      throw new Error(
        'LLMInputBuilder.model() is required before build(); use modelAdapterFor(modelId) or a provider adapter.'
      );
    }

    const adapter = this.s.modelAdapter;
    const coteraModel = llm.getModel(adapter.modelId);
    if (coteraModel === undefined) {
      throw new Error(`Model ${adapter.modelId} does not exist`);
    }

    const { messages, toolKeys, system, cacheKey } =
      await this.resolveBuildMessages(renderer);
    const finalizedMessages = adapter
      .finalizeMessages(messages)
      .filter((message) => message.role !== 'system');

    const draft: ModelInputDraft = {
      messages: finalizedMessages,
      system: system ?? undefined,
      toolSet: this.s.tools,
      streamTextOutput: this.s.streamTextOutput,
      outputShape: this.s.outputShape,
      baseCallSettings: this.pickBaseCallSettings(),
    };

    const callSettings = adapter.getCallSettings(draft.baseCallSettings);
    const providerOptions = adapter.mergeProviderOptions(
      draft.baseCallSettings?.providerOptions,
      callSettings
    );

    const streamTextOutputFromShape =
      draft.outputShape === undefined
        ? undefined
        : adapter.buildStreamTextOutputFromShape(draft.outputShape);

    const streamTextOutput =
      draft.streamTextOutput ?? streamTextOutputFromShape?.output;

    const llmInput: LLMInput = {
      messages: finalizedMessages,
      system: draft.system,
      output: streamTextOutput,
      tools: draft.toolSet,
      temperature: callSettings.temperature,
      topP: callSettings.topP,
      topK: callSettings.topK,
      presencePenalty: callSettings.presencePenalty,
      frequencyPenalty: callSettings.frequencyPenalty,
      stopSequences: callSettings.stopSequences,
      seed: callSettings.seed,
      maxOutputTokens: callSettings.maxOutputTokens,
      maxRetries: callSettings.maxRetries,
      providerOptions,
    };

    return {
      modelId: adapter.modelId,
      llmInput,
      cacheKey,
      toolKeys,
      streamTextOutputFromShape,
    };
  }

  private pickBaseCallSettings(): LlmCallSettings | undefined {
    const settings: LlmCallSettings = {
      temperature: this.s.temperature,
      topP: this.s.topP,
      topK: this.s.topK,
      presencePenalty: this.s.presencePenalty,
      frequencyPenalty: this.s.frequencyPenalty,
      stopSequences: this.s.stopSequences,
      seed: this.s.seed,
      maxOutputTokens: this.s.maxOutputTokens,
      maxRetries: this.s.maxRetries,
      providerOptions: this.s.providerOptions,
    };
    return Object.values(settings).some((value) => value !== undefined)
      ? settings
      : undefined;
  }

  private async resolveBuildMessages(renderer: LlmBuildRenderer): Promise<{
    messages: ModelMessage[];
    toolKeys: readonly string[];
    system: string | null;
    cacheKey: string;
  }> {
    const conversationRenderer: Renderer = renderer(this);
    const ctx = this.createRenderContext();
    const messageInstances = this.getMessages();
    const cacheKey =
      conversationRenderer.messagesCacheKey !== undefined
        ? conversationRenderer.messagesCacheKey(messageInstances, ctx)
        : this.fallbackMessagesCacheKey();
    return {
      messages: await conversationRenderer.conversationMessages(ctx),
      toolKeys: this.getResolvedToolKeys(),
      system: await conversationRenderer.system(ctx),
      cacheKey,
    };
  }

  /** Test renderers without {@link Renderer.messagesCacheKey}. */
  private fallbackMessagesCacheKey(): string {
    const parts: string[] = [];
    const system = this.getSystem();
    if (system !== null) {
      parts.push(system.stableCacheKey());
    }
    parts.push(...this.getMessages().map((m) => m.stableCacheKey()));
    return objectHash(parts);
  }
}

/** {@link LLMInput} without `tools` — spread into {@link streamText}, then pass `tools` separately. */
export type LlmInputStreamSpread = Omit<LLMInput, 'tools'>;

/** Omit `tools` when merging call-site streamText overrides (callbacks, abortSignal, etc.). */
export function withoutToolSetStreamOptions(
  input: LLMInput
): LlmInputStreamSpread {
  const { tools: _tools, ...rest } = input;
  return rest;
}

/**
 * Spread {@link LLMInput} into a full {@link streamText} argument object.
 */
export type StreamTextParamsFromLlmInput = {
  model: LanguageModel;
} & LLMInput;

export function toStreamTextParams(
  model: LanguageModel,
  input: LLMInput
): StreamTextParamsFromLlmInput {
  return { model, ...input };
}

export function llmInputBuilder(): LLMInputBuilder {
  return new LLMInputBuilder();
}
